"use strict";

if (!customElements.get("s-filter-modal")) {
  customElements.define(
    "s-filter-modal",
    class extends HTMLElement {
      connectedCallback() {
        this.overlay = this.querySelector(".js-filter-modal__overlay");
        this.modal = this.querySelector(".js-filter-modal__modal");
        this.form = this.querySelector("form");
        this.closeButtons = Array.from(this.querySelectorAll(".js-filter-modal__close-filter"));
        this.items = Array.from(this.querySelectorAll(".js-filter-modal__item"));
        this.priceRanges = Array.from(this.querySelectorAll(".js-filter-modal__price-slider"));

        this.openModal = this.openModal.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.handleItemToggle = this.handleItemToggle.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);

        this.closeButtons.forEach((button) => button.addEventListener("click", this.closeModal));
        this.items.forEach((item) => {
          const trigger = item.querySelector(".js-filter-modal__item-inner");
          if (trigger) {
            trigger.addEventListener("click", this.handleItemToggle);
          }
        });
        this.priceRanges.forEach((range) => this.initializePriceRange(range));
        if (this.form) {
          this.form.addEventListener("submit", this.handleFormSubmit);
        }

        if (window.PubSub && typeof window.PubSub.subscribe === "function") {
          this.openFilterUnsubscribe = window.PubSub.subscribe("open-filter", this.openModal);
          this.closeFilterUnsubscribe = window.PubSub.subscribe("close-filter", this.closeModal);
        }
      }

      disconnectedCallback() {
        this.closeButtons.forEach((button) => button.removeEventListener("click", this.closeModal));
        this.items.forEach((item) => {
          const trigger = item.querySelector(".js-filter-modal__item-inner");
          if (trigger) {
            trigger.removeEventListener("click", this.handleItemToggle);
          }
        });
        if (this.form) {
          this.form.removeEventListener("submit", this.handleFormSubmit);
        }

        if (this.openFilterUnsubscribe) {
          this.openFilterUnsubscribe();
        }

        if (this.closeFilterUnsubscribe) {
          this.closeFilterUnsubscribe();
        }
      }

      initializePriceRange(range) {
        const parent = range.closest(".s-filter-modal__range-outer");
        const minInput = parent ? parent.querySelector(".js-filter-modal__range-number-input-min") : null;
        const maxInput = parent ? parent.querySelector(".js-filter-modal__range-number-input-max") : null;
        const minSlider = range.querySelector(".js-filter-modal__price-slider-input-min");
        const maxSlider = range.querySelector(".js-filter-modal__price-slider-input-max");
        const progress = range.querySelector(".js-filter-modal__price-slider-progress");
        const rangeMin = Number.parseFloat(range.dataset.min || "0");
        const rangeMax = Number.parseFloat(range.dataset.max || "0");

        if (!minInput || !maxInput || !minSlider || !maxSlider || !progress) {
          return;
        }

        const formatValue = (value) => {
          const roundedValue = Math.round(value * 100) / 100;
          if (Number.isInteger(roundedValue)) {
            return `${roundedValue}`;
          }

          return roundedValue.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
        };

        const clampValues = (minValue, maxValue, source = "max") => {
          let nextMin = Number.isFinite(minValue) ? minValue : rangeMin;
          let nextMax = Number.isFinite(maxValue) ? maxValue : rangeMax;

          nextMin = Math.max(rangeMin, Math.min(nextMin, rangeMax));
          nextMax = Math.max(rangeMin, Math.min(nextMax, rangeMax));

          if (nextMin > nextMax) {
            if (source === "min") {
              nextMax = nextMin;
            } else {
              nextMin = nextMax;
            }
          }

          return [nextMin, nextMax];
        };

        const updateProgress = (minValue, maxValue) => {
          const safeRange = rangeMax - rangeMin || 1;
          const minPercent = ((minValue - rangeMin) / safeRange) * 100;
          const maxPercent = ((maxValue - rangeMin) / safeRange) * 100;

          progress.style.left = `${minPercent}%`;
          progress.style.right = `${100 - maxPercent}%`;
        };

        const syncAll = (minValue, maxValue, source = "max") => {
          const [nextMin, nextMax] = clampValues(minValue, maxValue, source);

          minInput.value = formatValue(nextMin);
          maxInput.value = formatValue(nextMax);
          minSlider.value = `${nextMin}`;
          maxSlider.value = `${nextMax}`;
          updateProgress(nextMin, nextMax);
        };

        const commitNumberInputs = (source) => {
          const minValue = minInput.value.trim() === "" ? Number.parseFloat(minSlider.value) : Number.parseFloat(minInput.value);
          const maxValue = maxInput.value.trim() === "" ? Number.parseFloat(maxSlider.value) : Number.parseFloat(maxInput.value);
          syncAll(minValue, maxValue, source);
        };

        minSlider.addEventListener("input", () => {
          syncAll(Number.parseFloat(minSlider.value), Number.parseFloat(maxSlider.value), "min");
        });

        maxSlider.addEventListener("input", () => {
          syncAll(Number.parseFloat(minSlider.value), Number.parseFloat(maxSlider.value), "max");
        });

        minInput.addEventListener("change", () => {
          commitNumberInputs("min");
        });

        maxInput.addEventListener("change", () => {
          commitNumberInputs("max");
        });

        minInput.addEventListener("blur", () => {
          commitNumberInputs("min");
        });

        maxInput.addEventListener("blur", () => {
          commitNumberInputs("max");
        });

        syncAll(
          Number.parseFloat(minInput.value || minSlider.value),
          Number.parseFloat(maxInput.value || maxSlider.value),
          "max"
        );
      }

      openModal() {
        if (this.overlay) {
          this.overlay.classList.add("is-active");
        }

        if (this.modal) {
          this.modal.classList.add("is-active");
        }

        document.body.classList.add("filter-modal--is-active");
      }

      closeModal(event) {
        if (event) {
          event.preventDefault();
        }

        if (this.overlay) {
          this.overlay.classList.remove("is-active");
        }

        if (this.modal) {
          this.modal.classList.remove("is-active");
        }

        document.body.classList.remove("filter-modal--is-active");
      }

      handleItemToggle(event) {
        const item = event.currentTarget.closest(".js-filter-modal__item");
        const values = item ? item.querySelector(".js-filter-modal__item-values") : null;

        if (!item || !values) {
          return;
        }

        const isActive = item.classList.contains("is-active");
        item.classList.toggle("is-active", !isActive);
        values.style.maxHeight = isActive ? "0px" : `${values.scrollHeight}px`;
      }

      handleFormSubmit() {
        this.priceRanges.forEach((range) => {
          const parent = range.closest(".s-filter-modal__range-outer");
          const minInput = parent ? parent.querySelector(".js-filter-modal__range-number-input-min") : null;
          const maxInput = parent ? parent.querySelector(".js-filter-modal__range-number-input-max") : null;
          const rangeMin = Number.parseFloat(range.dataset.min || "0");
          const rangeMax = Number.parseFloat(range.dataset.max || "0");
          const currentMin = minInput ? Number.parseFloat(minInput.value || `${rangeMin}`) : rangeMin;
          const currentMax = maxInput ? Number.parseFloat(maxInput.value || `${rangeMax}`) : rangeMax;
          const isFullRange = currentMin === rangeMin && currentMax === rangeMax;

          if (minInput) {
            minInput.disabled = isFullRange;
          }

          if (maxInput) {
            maxInput.disabled = isFullRange;
          }
        });
      }
    }
  );
}
